import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { Filter, RotateCcw, Plus, Trash2 } from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { useMapStore } from '../../store/map.store';
import TileWMS from 'ol/source/TileWMS';
import ImageWMS from 'ol/source/ImageWMS';

interface FilterCondition {
  id: number;
  property: string;
  operator: string;
  value: string;
}

interface FilterPanelProps {
  onClose: () => void;
}

const OPERATORS = [
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
  { value: 'LIKE', label: 'LIKE' },
  { value: 'ILIKE', label: 'ILIKE' },
  { value: 'IS NULL', label: 'IS NULL' },
  { value: 'IS NOT NULL', label: 'IS NOT NULL' },
];

let nextCondId = 1;

export const FilterPanel: React.FC<FilterPanelProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const mapInstance = useMapStore((s) => s.mapInstance);
  const [layers, setLayers] = useState<{ name: string; label: string }[]>([]);
  const [selectedLayer, setSelectedLayer] = useState('');
  const [conditions, setConditions] = useState<FilterCondition[]>([
    { id: nextCondId++, property: '', operator: '=', value: '' },
  ]);
  const [logicOp, setLogicOp] = useState<'AND' | 'OR'>('AND');

  useEffect(() => {
    if (!mapInstance) return;
    const layerList: { name: string; label: string }[] = [];
    mapInstance.getLayers().forEach((layer: any) => {
      const src = layer.getSource?.();
      if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
        const name = layer.get('name') || '';
        if (name && name !== 'wms-layer') layerList.push({ name, label: name });
      }
    });
    setLayers(layerList);
    if (layerList.length > 0 && !selectedLayer) setSelectedLayer(layerList[0].name);
  }, [mapInstance]);

  const addCondition = () => {
    setConditions((prev) => [
      ...prev,
      { id: nextCondId++, property: '', operator: '=', value: '' },
    ]);
  };

  const removeCondition = (id: number) => {
    setConditions((prev) => prev.filter((c) => c.id !== id));
  };

  const updateCondition = (id: number, field: keyof FilterCondition, value: string) => {
    setConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    );
  };

  const applyFilter = useCallback(() => {
    if (!mapInstance || !selectedLayer) return;
    const validConditions = conditions.filter((c) => c.property.trim());
    if (validConditions.length === 0) {
      clearFilter();
      return;
    }
    // A quote inside a value is doubled, as CQL writes it; a column name keeps only what a column name can hold.
    const text = (v: string) => `'${v.replace(/'/g, "''")}'`;
    const cqlParts = validConditions.map((c) => {
      c = { ...c, property: c.property.replace(/[^A-Za-z0-9_]/g, '') };
      if (c.operator === 'IS NULL' || c.operator === 'IS NOT NULL') {
        return `${c.property} ${c.operator}`;
      }
      if (c.operator === 'LIKE' || c.operator === 'ILIKE') {
        return `${c.property} ${c.operator} ${text(c.value)}`;
      }
      const numVal = Number(c.value);
      if (!isNaN(numVal) && c.value.trim()) {
        return `${c.property} ${c.operator} ${numVal}`;
      }
      return `${c.property} ${c.operator} ${text(c.value)}`;
    });
    const cqlFilter = cqlParts.join(` ${logicOp} `);
    mapInstance.getLayers().forEach((layer: any) => {
      if (layer.get('name') === selectedLayer) {
        const src = layer.getSource?.();
        if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
          src.updateParams({ CQL_FILTER: cqlFilter });
        }
      }
    });
  }, [mapInstance, selectedLayer, conditions, logicOp]);

  const clearFilter = useCallback(() => {
    if (!mapInstance || !selectedLayer) return;
    mapInstance.getLayers().forEach((layer: any) => {
      if (layer.get('name') === selectedLayer) {
        const src = layer.getSource?.();
        if (src && (src instanceof TileWMS || src instanceof ImageWMS)) {
          src.updateParams({ CQL_FILTER: undefined });
        }
      }
    });
  }, [mapInstance, selectedLayer]);

  return (
    <DraggablePanel
      title={t('filter.title')}
      onClose={onClose}
      icon={<Filter size={14} />}
      width={340}
    >
      <div className="p-3 space-y-3">
        {/* Layer selector */}
        <div>
          <span className="text-[10px] text-gn-secondary font-medium block mb-1">{t('style.layer')}</span>
          <select
            value={selectedLayer}
            onChange={(e) => setSelectedLayer(e.target.value)}
            className="w-full px-2 py-1.5 text-xs text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
          >
            {layers.map((l) => (
              <option key={l.name} value={l.name}>{l.label}</option>
            ))}
          </select>
        </div>

        {/* Logic operator */}
        <div className="flex gap-3 text-xs text-gn-primary">
          <label className="flex items-center gap-1">
            <input type="radio" checked={logicOp === 'AND'} onChange={() => setLogicOp('AND')} className="accent-blue-500" />
            {t('filter.all')}
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" checked={logicOp === 'OR'} onChange={() => setLogicOp('OR')} className="accent-blue-500" />
            {t('filter.any')}
          </label>
        </div>

        {/* Conditions */}
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {conditions.map((cond) => (
            <div key={cond.id} className="flex gap-1 items-center">
              <input
                type="text"
                placeholder={t('filter.column')}
                value={cond.property}
                onChange={(e) => updateCondition(cond.id, 'property', e.target.value)}
                className="flex-1 px-1.5 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              />
              <select
                value={cond.operator}
                onChange={(e) => updateCondition(cond.id, 'operator', e.target.value)}
                className="px-1 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
              >
                {OPERATORS.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              {cond.operator !== 'IS NULL' && cond.operator !== 'IS NOT NULL' && (
                <input
                  type="text"
                  placeholder={t('filter.value')}
                  value={cond.value}
                  onChange={(e) => updateCondition(cond.id, 'value', e.target.value)}
                  className="flex-1 px-1.5 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
                />
              )}
              {conditions.length > 1 && (
                <button
                  onClick={() => removeCondition(cond.id)}
                  className="p-0.5 text-gn-danger hover:text-gn-danger"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={addCondition}
          className="flex items-center gap-1 text-[11px] text-gn-blue hover:text-gn-blue"
        >
          <Plus size={12} /> {t('filter.add')}
        </button>

        {/* Action buttons */}
        <div className="flex gap-2">
          <button
            onClick={applyFilter}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium bg-gn-blue text-gn-inverse hover:bg-gn-blue transition-colors"
          >
            <Filter size={12} /> {t('filter.apply')}
          </button>
          <button
            onClick={clearFilter}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium border border-gn-light text-gn-primary hover:bg-gn-tertiary transition-colors"
          >
            <RotateCcw size={12} /> {t('buttons.clear')}
          </button>
        </div>
      </div>
    </DraggablePanel>
  );
};
